import { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'motion/react';
import Cartao from '@/components/ui/Cartao';
import Botao from '@/components/ui/Botao';
import EstadoVazio from '@/components/ui/EstadoVazio';
import ComprovanteDespesa from '@/pages/gerenciar/direcao/components/caixa/ComprovanteDespesa';
import { baixarCSV, formatarReais } from '@/pages/gerenciar/utils';
import { dataSemHora, ehSaida, paraDataLocal } from '@/lib/extrato';
import type { ExtratoLinha } from './tipos';
import { dataBR, dataCurta, horaBR, rotuloForma } from './rotulos';

const PERIODOS = [
  { valor: 'todos', rotulo: 'Tudo' },
  { valor: 'hoje', rotulo: 'Hoje' },
  { valor: '7dias', rotulo: '7 dias' },
  { valor: '30dias', rotulo: '30 dias' },
] as const;

type Periodo = (typeof PERIODOS)[number]['valor'];

function filtrar(linhas: ExtratoLinha[], periodo: Periodo): ExtratoLinha[] {
  if (periodo === 'todos') return linhas;
  const agora = new Date();
  const inicio = new Date(agora);
  if (periodo === 'hoje') inicio.setHours(0, 0, 0, 0);
  else if (periodo === '7dias') inicio.setDate(agora.getDate() - 7);
  else if (periodo === '30dias') inicio.setDate(agora.getDate() - 30);
  return linhas.filter((l) => paraDataLocal(l.quando).getTime() >= inicio.getTime());
}

function hojeISO(): string {
  const d = new Date();
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mes}-${dia}`;
}

// Data da linha do extrato: datas sem hora (despesa) mostram só "dd/mm/aaaa";
// movimentações com hora mostram "dd/mm/aaaa HH:MM".
function quandoDe(l: ExtratoLinha): string {
  return dataSemHora(l.quando) ?? `${dataBR(l.quando)} ${horaBR(l.quando)}`;
}

// Pedido de fichas: "Pedido nº 1003 · Nome" + "Loja · pix · data · Nome" vira
// "Fichas · Nome" + "Pix · 20/10 12:20". O nome aparece uma vez só.
function nomeDaPessoa(l: ExtratoLinha): string {
  const partes = (l.descricao ?? '').split('·').map((p) => p.trim());
  if (partes.length > 1 && partes[partes.length - 1]) return partes[partes.length - 1];
  return (l.lancado_por ?? '').trim();
}

function ehPedidoDeFichas(l: ExtratoLinha): boolean {
  return l.origem === 'loja';
}

export default function BlocoExtrato({
  linhas,
  slug,
  eventoId,
  aoMudar,
}: {
  linhas: ExtratoLinha[];
  slug: string;
  eventoId: number;
  aoMudar: () => void;
}) {
  const [periodo, setPeriodo] = useState<Periodo>('todos');
  const [soSemComprovante, setSoSemComprovante] = useState(false);

  // Linha nova (tempo real): entra pelo topo com fade e destaque amarelo
  // claro que some em 1,5 s. Só realça — o valor não muda.
  const chaveTopoRef = useRef<string | number | null>(null);
  const [destaque, setDestaque] = useState<string | number | null>(null);
  const topoChave = linhas[0]?.chave ?? null;
  useEffect(() => {
    const anterior = chaveTopoRef.current;
    chaveTopoRef.current = topoChave;
    if (anterior === null || topoChave === null || anterior === topoChave) return;
    setDestaque(topoChave);
    const t = window.setTimeout(() => setDestaque(null), 1500);
    return () => window.clearTimeout(t);
  }, [topoChave]);

  const semComprovante = useMemo(
    () => linhas.filter((l) => l.origem === 'despesa' && !l.comprovante_path),
    [linhas],
  );

  const base = soSemComprovante ? semComprovante : linhas;
  const filtradas = useMemo(() => filtrar(base, periodo), [base, periodo]);

  function exportar() {
    const cabecalho = ['Data', 'Descrição', 'Categoria', 'Forma', 'Quem', 'Valor'];
    const corpo = filtradas.map((l) => [
      dataSemHora(l.quando) ?? dataBR(l.quando),
      l.descricao ?? '',
      l.categoria ?? '',
      l.forma ?? '',
      l.lancado_por ?? '',
      formatarReais(Number(l.valor ?? 0)),
    ]);
    baixarCSV(`financeiro-${slug}-${hojeISO()}.csv`, [cabecalho, ...corpo]);
  }

  return (
    <Cartao className="p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-[17px] font-bold text-foreground-950">Extrato</h2>
        <Botao variante="secundario" icone="ri-download-2-line" onClick={exportar}>
          Exportar CSV
        </Botao>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1 rounded-full bg-secondary-100 px-1 py-1 sm:inline-flex">
          {PERIODOS.map((p) => (
            <button
              key={p.valor}
              type="button"
              onClick={() => setPeriodo(p.valor)}
              className={`flex-1 cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-[13px] font-semibold transition-colors sm:flex-none ${
                periodo === p.valor
                  ? 'bg-white text-foreground-950'
                  : 'text-secondary-600 hover:text-foreground-900'
              }`}
            >
              {p.rotulo}
            </button>
          ))}
        </div>

        {semComprovante.length > 0 && (
          <button
            type="button"
            onClick={() => setSoSemComprovante((v) => !v)}
            className={`inline-flex min-h-[44px] cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 text-[13px] font-semibold transition-colors ${
              soSemComprovante
                ? 'bg-warning-100 text-warning-800'
                : 'text-foreground-500 hover:bg-background-100 hover:text-foreground-900'
            }`}
          >
            <i className="ri-attachment-2" />
            {semComprovante.length} sem comprovante
            {soSemComprovante && <span className="font-normal">· mostrando só essas</span>}
          </button>
        )}
      </div>

      {linhas.length === 0 ? (
        <div className="mt-3">
          <EstadoVazio
            icone="ri-coins-line"
            titulo="Nada no extrato ainda"
            descricao="Quando entrar ou sair dinheiro, cada movimentação aparece aqui."
          />
        </div>
      ) : filtradas.length === 0 ? (
        <p className="mt-4 text-[14px] text-foreground-500">Nenhuma movimentação nesse período.</p>
      ) : (
        <ul className="mt-2 divide-y divide-secondary-100">
          {filtradas.map((l) => {
            const saida = ehSaida(l.tipo);
            const fichas = ehPedidoDeFichas(l) && !saida;
            const nome = fichas ? nomeDaPessoa(l) : '';
            const titulo = fichas ? (nome ? `Fichas · ${nome}` : 'Fichas') : l.descricao;
            const apoio = fichas
              ? [rotuloForma(l.forma), `${dataCurta(l.quando)} ${horaBR(l.quando)}`.trim()]
                  .filter(Boolean)
                  .join(' · ')
              : [l.categoria, l.forma, quandoDe(l), l.lancado_por].filter(Boolean).join(' · ');
            const novo = destaque !== null && l.chave === destaque;
            return (
              <motion.li
                key={l.chave}
                initial={novo ? { opacity: 0.4, y: -6 } : false}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className={`-mx-2 flex items-start justify-between gap-3 rounded-xl px-2 py-3 transition-colors duration-700 ${
                  novo ? 'bg-warning-100' : 'bg-transparent'
                }`}
              >
                <div className="min-w-0">
                  <p className="text-[15px] font-medium text-foreground-900">{titulo}</p>
                  <p className="mt-0.5 text-[12px] text-foreground-500">{apoio}</p>
                  {l.detalhe && <p className="mt-0.5 text-[12px] text-foreground-400">{l.detalhe}</p>}

                  {l.origem === 'despesa' && (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <ComprovanteDespesa
                        eventoId={eventoId}
                        despesaId={Number(l.lancamento_id)}
                        comprovantePath={l.comprovante_path ?? null}
                        comprovanteNome={l.comprovante_nome ?? null}
                        comprovanteTipo={l.comprovante_tipo ?? null}
                        aoMudar={aoMudar}
                      />
                    </div>
                  )}
                </div>
                <span
                  className={`shrink-0 whitespace-nowrap text-[15px] font-bold tabular-nums ${
                    saida ? 'text-danger-600' : 'text-success-700'
                  }`}
                >
                  {saida ? '-' : '+'} {formatarReais(Number(l.valor ?? 0))}
                </span>
              </motion.li>
            );
          })}
        </ul>
      )}
    </Cartao>
  );
}